/**
 * 발달기록 체계 — 명세서 §6
 *
 * 이 파일은 "코드가 아닌 데이터"로 관리되는 발달 체계의 단일 원천(single source of truth)이다.
 * DB 시드(DevelopmentPeriod / DevelopmentDomain)는 여기서 생성한 값을 그대로 적재한다(§6.3, §6.5).
 *
 * 언어 규칙: 모든 표시 문자열은 강점 기반(strength-based)·진행형으로만 작성한다(§6.1-1, §6.2).
 */
export const DEVELOPMENT_STAGES = [
    {
        code: 'L1',
        level: 1,
        name: '씨앗',
        definition: '새로운 활동·자극을 만나 지켜보는 단계',
        theory: '노출·주의',
        color: '#DFF3EA',
        icon: 'seed',
    },
    {
        code: 'L2',
        level: 2,
        name: '새싹',
        definition: '관심을 갖고 보고 듣고 만지며 탐색하는 단계',
        theory: '능동 탐색',
        color: '#C6E9D9',
        icon: 'sprout',
    },
    {
        code: 'L3',
        level: 3,
        name: '떡잎',
        definition: '교사와 함께 처음 시도해 보는 단계',
        theory: 'ZPD — 전면 지원',
        color: '#A9DCC5',
        icon: 'cotyledon',
    },
    {
        code: 'L4',
        level: 4,
        name: '줄기',
        definition: '약간의 도움이나 격려로 해내는 단계',
        theory: 'ZPD — 부분 지원(비계)',
        color: '#8ACFB1',
        icon: 'stem',
    },
    {
        code: 'L5',
        level: 5,
        name: '꽃봉오리',
        definition: '스스로 해보기 시작하는 단계',
        theory: '독립 수행 진입',
        color: '#66BE99',
        icon: 'bud',
    },
    {
        code: 'L6',
        level: 6,
        name: '꽃',
        definition: '여러 상황에서 안정적으로 해내는 단계',
        theory: '숙달·일반화',
        color: '#48A681',
        icon: 'flower',
    },
    {
        code: 'L7',
        level: 7,
        name: '열매',
        definition: '배운 것을 새로운 상황에 응용하고 친구를 돕는 단계',
        theory: '전이·확장',
        color: '#2E8B6D',
        icon: 'fruit',
    },
];
export function getStage(level) {
    const stage = DEVELOPMENT_STAGES.find((s) => s.level === level);
    if (!stage) {
        throw new Error(`발달 단계는 1–7 사이여야 합니다. 받은 값: ${level}`);
    }
    return stage;
}
export function isDevelopmentLevel(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 7;
}
/* ------------------------------------------------------------------ *
 * §6.5 — 발달 영역 (앱 공통 6개 + 국가 과정 라벨 매핑)
 * ------------------------------------------------------------------ */
export const DEVELOPMENT_DOMAIN_CODES = [
    'PHYSICAL_GROSS',
    'PHYSICAL_FINE',
    'COMMUNICATION',
    'COGNITION',
    'SOCIAL_EMOTION',
    'SELF_CARE',
];
export const DEVELOPMENT_DOMAINS = [
    {
        code: 'PHYSICAL_GROSS',
        name: '신체 — 대근육',
        order: 1,
        icon: 'run',
        mapping: { standard0to2: '신체운동', nuri3to5: '신체운동·건강' },
    },
    {
        code: 'PHYSICAL_FINE',
        name: '신체 — 소근육',
        order: 2,
        icon: 'hand',
        mapping: { standard0to2: '신체운동', nuri3to5: '신체운동·건강' },
    },
    {
        code: 'COMMUNICATION',
        name: '의사소통·언어',
        order: 3,
        icon: 'speech',
        mapping: { standard0to2: '의사소통', nuri3to5: '의사소통' },
    },
    {
        code: 'COGNITION',
        name: '인지·탐구',
        order: 4,
        icon: 'search',
        mapping: { standard0to2: '자연탐구', nuri3to5: '자연탐구' },
    },
    {
        code: 'SOCIAL_EMOTION',
        name: '사회관계·정서',
        order: 5,
        icon: 'heart',
        mapping: { standard0to2: '사회관계 / 예술경험', nuri3to5: '사회관계 / 예술경험' },
    },
    {
        code: 'SELF_CARE',
        name: '기본생활·자조',
        order: 6,
        icon: 'sparkle',
        mapping: { standard0to2: '기본생활', nuri3to5: '신체운동·건강(생활습관)' },
    },
];
/** 보고서(생활기록부·구간 리포트) 출력 시 연령에 맞는 국가 과정 영역명으로 라벨 매핑(§6.5) */
export function nationalCurriculumLabel(code, ageMonths) {
    const domain = DEVELOPMENT_DOMAINS.find((d) => d.code === code);
    if (!domain)
        return code;
    return ageMonths < 36 ? domain.mapping.standard0to2 : domain.mapping.nuri3to5;
}
/* ------------------------------------------------------------------ *
 * §6.3 — 연령 구간 체계
 * ------------------------------------------------------------------ */
export const AGE_BANDS = {
    /** 0세(영아 초기) 0–11개월 — 1개월 단위 */
    INFANT_EARLY: 'INFANT_EARLY',
    /** 영아기 12–35개월 — 3개월 단위 */
    INFANT: 'INFANT',
    /** 유아기 36–83개월 — 6개월 단위 */
    TODDLER: 'TODDLER',
    /** 아동기 84개월~ — 12개월 단위 */
    CHILD: 'CHILD',
};
export const AGE_BAND_LABELS = {
    INFANT_EARLY: '0세(영아 초기)',
    INFANT: '영아기',
    TODDLER: '유아기',
    CHILD: '아동기',
};
const AGE_BAND_SPECS = [
    { band: AGE_BANDS.INFANT_EARLY, fromMonth: 0, toMonth: 11, unitMonths: 1 },
    { band: AGE_BANDS.INFANT, fromMonth: 12, toMonth: 35, unitMonths: 3 },
    { band: AGE_BANDS.TODDLER, fromMonth: 36, toMonth: 83, unitMonths: 6 },
    // 아동기는 상한이 없으므로 만 7–9세(84–119개월) 3개 구간을 시드로 생성한다.
    { band: AGE_BANDS.CHILD, fromMonth: 84, toMonth: 119, unitMonths: 12, seedCount: 3 },
];
function pad(n) {
    return String(n).padStart(3, '0');
}
/**
 * §6.3 구간 정의를 결정적으로 생성한다.
 * 0–11개월 12구간 / 12–35개월 8구간 / 36–83개월 8구간 / 84개월~ 연 1구간.
 */
export function buildDevelopmentPeriods() {
    const periods = [];
    let order = 1;
    for (const spec of AGE_BAND_SPECS) {
        const upper = spec.toMonth ?? spec.fromMonth + spec.unitMonths * (spec.seedCount ?? 1) - 1;
        for (let from = spec.fromMonth; from <= upper; from += spec.unitMonths) {
            const to = Math.min(from + spec.unitMonths - 1, upper);
            periods.push({
                code: `AGE_${pad(from)}_${pad(to)}`,
                band: spec.band,
                label: spec.unitMonths === 1 ? `${from}개월` : `${from}–${to}개월`,
                fromMonth: from,
                toMonth: to,
                unitMonths: spec.unitMonths,
                order: order++,
            });
        }
    }
    return periods;
}
export const DEVELOPMENT_PERIODS = buildDevelopmentPeriods();
/** 개월 수로 현재 발달 구간을 판정한다. 상한을 넘어서면 마지막 구간을 반환. */
export function findPeriodForMonths(ageMonths) {
    const match = DEVELOPMENT_PERIODS.find((p) => ageMonths >= p.fromMonth && ageMonths <= p.toMonth);
    if (match)
        return match;
    const last = DEVELOPMENT_PERIODS[DEVELOPMENT_PERIODS.length - 1];
    return ageMonths < 0 ? DEVELOPMENT_PERIODS[0] : last;
}
/** 개월 수로 연령 밴드를 판정한다. 반 기본 컨디션 태그 세트 결정 등에 사용(§5.3). */
export function findAgeBandForMonths(ageMonths) {
    if (ageMonths < 12)
        return AGE_BANDS.INFANT_EARLY;
    if (ageMonths < 36)
        return AGE_BANDS.INFANT;
    if (ageMonths < 84)
        return AGE_BANDS.TODDLER;
    return AGE_BANDS.CHILD;
}
/**
 * 기록 마감 권장 기한 = 구간 종료 + 1개월 (§6.3)
 * @param periodEndDate 해당 원아가 그 구간을 벗어나는 날짜
 */
export function recommendedDueDate(periodEndDate) {
    const due = new Date(periodEndDate.getTime());
    due.setMonth(due.getMonth() + 1);
    return due;
}
/* ------------------------------------------------------------------ *
 * 발달기록 상태 (§6.7, §10.1)
 * ------------------------------------------------------------------ */
export const RECORD_STATUS = {
    DRAFT: 'DRAFT',
    REVIEW: 'REVIEW',
    PUBLISHED: 'PUBLISHED',
};
export const RECORD_STATUS_LABELS = {
    DRAFT: '작성 중',
    REVIEW: '원장 검토 중',
    PUBLISHED: '가정 공개',
};
/** 관찰 항목 은행의 전문가 감수 상태 (§6.6 저작 프로세스) */
export const ITEM_REVIEW_STATUS = {
    DRAFT: 'DRAFT',
    REVIEWED: 'REVIEWED',
};
//# sourceMappingURL=development.js.map